/**
 * Chat transcript PDF export.
 *
 * The only entry point pages need: map whatever the page stores into
 * `PdfChatMessage[]`, pass a title and some metadata, and this produces a
 * paginated, selectable-text PDF and downloads it.
 */

import { jsPDF } from 'jspdf';
import { PdfLayout } from './renderer';
import { MarkdownDocument, parseMarkdown, parsePlainText } from './markdown';
import { PDF_COLOR, PDF_FONT, PDF_SPACE } from './theme';
import {
  ChatTranscriptPdfOptions,
  PdfAttachment,
  PdfChatMessage,
  PdfChatRole,
  PdfLink,
} from './types';
import { collapseWhitespace, formatDisplayDate, sanitizeForPdf, slugify, toFileDate } from './text';

const DEFAULT_ROLE_LABELS: Record<PdfChatRole, string> = {
  user: 'You',
  assistant: 'Enviro AI',
};

const DEFAULT_BRAND = { text: 'enviro.ai', accentFrom: 'enviro'.length };
const MESSAGE_INDENT = 14;
const IMAGE_LOAD_TIMEOUT_MS = 4000;

interface ResolvedImage {
  dataUrl: string;
  format: string;
  width: number;
  height: number;
}

const imageFormat = (dataUrl: string, mime?: string): string => {
  const source = /^data:([^;,]+)/.exec(dataUrl);
  const type = (source ? source[1] : mime || '').toLowerCase();
  if (type.indexOf('png') !== -1) return 'PNG';
  if (type.indexOf('webp') !== -1) return 'WEBP';
  if (type.indexOf('gif') !== -1) return 'GIF';
  return 'JPEG';
};

const isEmbeddableImage = (attachment: PdfAttachment): boolean =>
  !!attachment.dataUrl &&
  attachment.dataUrl.indexOf('data:image/') === 0 &&
  /^image\/(png|jpe?g|webp|gif)$/i.test(attachment.type || '');

/**
 * Reads an image's intrinsic size. Resolves `null` rather than rejecting so one
 * bad attachment cannot fail the export; the timeout covers data URLs that
 * never fire either event (jsdom, malformed payloads).
 */
const resolveImage = (attachment: PdfAttachment): Promise<ResolvedImage | null> =>
  new Promise((resolve) => {
    const dataUrl = attachment.dataUrl as string;
    let settled = false;

    const finish = (value: ResolvedImage | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const timer = setTimeout(() => finish(null), IMAGE_LOAD_TIMEOUT_MS);
    const image = new Image();

    image.onload = () => {
      clearTimeout(timer);
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      if (!width || !height) {
        finish(null);
        return;
      }
      // Treat the bitmap as 96dpi so screenshots land at a sensible print size.
      finish({
        dataUrl,
        format: imageFormat(dataUrl, attachment.type),
        width: (width * 72) / 96,
        height: (height * 72) / 96,
      });
    };
    image.onerror = () => {
      clearTimeout(timer);
      finish(null);
    };

    image.src = dataUrl;
  });

const parseMessage = (message: PdfChatMessage, linkFootnotes: boolean): MarkdownDocument => {
  const format = message.format || (message.role === 'assistant' ? 'markdown' : 'text');
  return format === 'markdown'
    ? parseMarkdown(message.content, linkFootnotes)
    : parsePlainText(message.content);
};

const formatTimestamp = (value: PdfChatMessage['timestamp']): string => {
  if (value === undefined || value === null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return '';
  return `${formatDisplayDate(date)}, ${date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
};

const renderHeader = (
  layout: PdfLayout,
  options: ChatTranscriptPdfOptions,
  title: string
): void => {
  const brand = options.brand === false ? null : options.brand || DEFAULT_BRAND;
  const label = options.documentLabel || 'Chat transcript';

  if (brand) {
    const accentFrom =
      typeof (brand as { accentFrom?: number }).accentFrom === 'number'
        ? (brand as { accentFrom?: number }).accentFrom!
        : brand.text.length;
    const head = sanitizeForPdf(brand.text.slice(0, accentFrom));
    const tail = sanitizeForPdf(brand.text.slice(accentFrom));

    layout.doc.setFont('helvetica', 'bold');
    layout.doc.setFontSize(PDF_FONT.wordmark);
    layout.setInk(PDF_COLOR.ink);
    layout.doc.text(head, layout.left, layout.y, { baseline: 'top' });
    const headWidth = layout.doc.getTextWidth(head);
    layout.setInk(PDF_COLOR.accent);
    layout.doc.text(tail, layout.left + headWidth, layout.y, { baseline: 'top' });
  }

  if (label) {
    layout.doc.setFont('helvetica', 'normal');
    layout.doc.setFontSize(PDF_FONT.tiny);
    layout.setInk(PDF_COLOR.faint);
    layout.doc.text(sanitizeForPdf(label.toUpperCase()), layout.right, layout.y + 4, {
      baseline: 'top',
      align: 'right',
      charSpace: 1,
    });
  }

  layout.space(PDF_FONT.wordmark * 1.6);

  layout.paragraph([{ text: title, bold: true }], {
    size: PDF_FONT.title,
    color: PDF_COLOR.ink,
    lineHeight: 1.3,
  });

  const meta = (options.meta || [])
    .map((item) => collapseWhitespace(item.value))
    .filter(Boolean)
    .join('  ·  ');

  if (meta) {
    layout.space(2);
    layout.line(meta, { size: PDF_FONT.small, color: PDF_COLOR.muted });
  }

  layout.space(PDF_SPACE.ruleGap);
  layout.rule(PDF_COLOR.rule);
  layout.space(PDF_SPACE.message);
};

const renderRoleLabel = (layout: PdfLayout, label: string, timestamp: string, accent: boolean) => {
  const size = PDF_FONT.roleLabel;
  const height = size * 1.7;
  layout.ensureSpace(height + PDF_FONT.body * PDF_FONT.lineHeight);

  layout.doc.setFont('helvetica', 'bold');
  layout.doc.setFontSize(size);
  layout.setInk(accent ? PDF_COLOR.accent : PDF_COLOR.muted);
  layout.doc.text(sanitizeForPdf(label.toUpperCase()), layout.left + MESSAGE_INDENT, layout.y, {
    baseline: 'top',
    charSpace: 0.8,
  });

  if (timestamp) {
    layout.doc.setFont('helvetica', 'normal');
    layout.doc.setFontSize(PDF_FONT.tiny);
    layout.setInk(PDF_COLOR.faint);
    layout.doc.text(sanitizeForPdf(timestamp), layout.right, layout.y, {
      baseline: 'top',
      align: 'right',
    });
  }

  layout.space(height);
};

const renderAttachments = (
  layout: PdfLayout,
  attachments: PdfAttachment[],
  images: Map<string, ResolvedImage | null>
): void => {
  attachments.forEach((attachment) => {
    const resolved = attachment.dataUrl ? images.get(attachment.dataUrl) : null;
    if (resolved) {
      layout.image(resolved, 200, MESSAGE_INDENT);
      layout.space(4);
      return;
    }
    layout.chip(attachment.name || 'attachment', MESSAGE_INDENT);
  });

  if (attachments.length) layout.space(PDF_SPACE.paragraph);
};

const renderLinkFootnotes = (layout: PdfLayout, links: PdfLink[]): void => {
  if (!links.length) return;

  layout.space(2);
  layout.line('LINKS', {
    size: PDF_FONT.tiny,
    color: PDF_COLOR.faint,
    bold: true,
    indent: MESSAGE_INDENT,
  });

  links.forEach((link) => {
    layout.paragraph(
      [
        { text: `${link.index}. `, bold: true },
        { text: sanitizeForPdf(link.href), href: link.href },
      ],
      { size: PDF_FONT.tiny, color: PDF_COLOR.muted, indent: MESSAGE_INDENT, lineHeight: 1.5 }
    );
  });
};

const resolveTitle = (options: ChatTranscriptPdfOptions): string => {
  const title = collapseWhitespace(options.title || '');
  if (title) return sanitizeForPdf(title);
  const firstUser = options.messages.find((message) => message.role === 'user');
  const fallback = firstUser ? collapseWhitespace(firstUser.content) : '';
  return sanitizeForPdf(fallback || 'Chat transcript');
};

/**
 * Builds the document without saving it — exposed for tests and for callers
 * that want to post-process or inspect the jsPDF instance.
 */
export const buildChatTranscriptPdf = async (
  options: ChatTranscriptPdfOptions
): Promise<{ doc: jsPDF; fileName: string }> => {
  const linkFootnotes = options.linkFootnotes !== false;
  const roleLabels = { ...DEFAULT_ROLE_LABELS, ...(options.roleLabels || {}) };
  const title = resolveTitle(options);
  const now = new Date();

  // Resolve every image up front: jsPDF's own layout pass is synchronous.
  const images = new Map<string, ResolvedImage | null>();
  const pending: Promise<void>[] = [];
  options.messages.forEach((message) => {
    (message.attachments || []).forEach((attachment) => {
      if (!isEmbeddableImage(attachment)) return;
      const key = attachment.dataUrl as string;
      if (images.has(key)) return;
      images.set(key, null);
      pending.push(
        resolveImage(attachment).then((resolved) => {
          images.set(key, resolved);
        })
      );
    });
  });
  await Promise.all(pending);

  const layout = new PdfLayout();
  renderHeader(layout, options, title);

  const messages = options.messages.filter(
    (message) => (message.content && message.content.trim()) || (message.attachments || []).length
  );

  if (!messages.length) {
    layout.line('This chat has no messages yet.', {
      size: PDF_FONT.body,
      color: PDF_COLOR.muted,
      italic: true,
    });
  }

  messages.forEach((message, index) => {
    const isAssistant = message.role === 'assistant';
    const parsed = parseMessage(message, linkFootnotes && isAssistant);
    const attachments = message.attachments || [];

    renderRoleLabel(
      layout,
      roleLabels[message.role] || message.role,
      formatTimestamp(message.timestamp),
      isAssistant
    );

    layout.withLeftRule(isAssistant ? PDF_COLOR.assistantRule : PDF_COLOR.userRule, 2, () => {
      if (attachments.length) renderAttachments(layout, attachments, images);
      layout.renderBlocks(parsed.blocks, MESSAGE_INDENT);
      if (linkFootnotes && isAssistant) renderLinkFootnotes(layout, parsed.links);
    });

    if (index < messages.length - 1) layout.space(PDF_SPACE.message);
  });

  layout.stampFooters(title);

  const fileName = options.fileName || `${slugify(title)}-${toFileDate(now)}.pdf`;

  return { doc: layout.doc, fileName };
};

/** Builds the transcript and triggers the browser download. */
export const exportChatTranscriptToPdf = async (
  options: ChatTranscriptPdfOptions
): Promise<void> => {
  const { doc, fileName } = await buildChatTranscriptPdf(options);
  doc.save(fileName);
};
